import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { mainSiteUrl } from '@/mocks/categories';

export default function BasetaoReview() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Basetao Review 2026: Is Basetao Legit, Safe, and Worth Using?',
            description: 'An honest, independent, and detailed review of Basetao covering platform experience, product selection, warehouse service quality, shipping costs, safety, and whether Basetao Spreadsheet is genuinely worth your time as an international shopper in 2026.',
            author: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
            datePublished: '2026-08-10',
            dateModified: '2026-08-10',
            image: 'https://readdy.ai/api/search-image?query=A%20professional%20product%20inspection%20scene%20with%20various%20fashion%20items%20laid%20out%20on%20a%20light%20wood%20table%2C%20a%20checklist%20clipboard%20and%20camera%20nearby%20suggesting%20quality%20review%20process%2C%20warm%20natural%20daylight%20from%20a%20large%20window%2C%20editorial%20photography%20with%20clean%20neutral%20tones%2C%20trustworthy%20and%20thorough%20aesthetic&width=1200&height=500&seq=review-hero-v2-2026&orientation=landscape',
            publisher: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
          }),
        }}
      />
      <main className="bg-background-50">
        <article>
          <header className="w-full pt-24 pb-12 md:pt-28 md:pb-16 bg-background-50">
            <div className="max-w-3xl mx-auto px-6 md:px-8">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
                <a href="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">Home</a>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium">Basetao Review</span>
              </nav>

              <span className="inline-block px-3 py-1 mb-4 text-xs font-medium tracking-wider uppercase rounded-full bg-accent-100 text-accent-700">
                Independent Review &middot; 2026
              </span>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-900 leading-tight mb-6">
                Basetao Review 2026: Is Basetao Legit, Safe, and Worth Using?
              </h1>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 max-w-2xl">
                An honest, independent, and detailed review of Basetao covering platform experience, product selection, warehouse service quality, shipping costs, safety, and whether Basetao Spreadsheet is genuinely worth your time as an international shopper in 2026.
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Updated August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  10 min read
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-shield-check-line"></i>
                  Independent Review
                </span>
              </div>
            </div>
          </header>

          <div className="relative w-full h-64 md:h-80 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20professional%20product%20inspection%20scene%20with%20various%20fashion%20items%20laid%20out%20on%20a%20light%20wood%20table%2C%20a%20checklist%20clipboard%20and%20camera%20nearby%20suggesting%20quality%20review%20process%2C%20warm%20natural%20daylight%20from%20a%20large%20window%2C%20editorial%20photography%20with%20clean%20neutral%20tones%2C%20trustworthy%20and%20thorough%20aesthetic&width=1200&height=500&seq=review-hero-v2-2026&orientation=landscape"
              alt="Basetao Review 2026 - Is Basetao Legit and Safe for International Shoppers"
              title="Basetao Review 2026"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent"></div>
          </div>

          <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14">
            <div className="prose-custom">

              <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 mb-10">
                <h4 className="font-heading font-semibold text-foreground-900 text-lg mb-4">Review Summary</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">Overall Rating</p>
                    <div className="flex items-center gap-1 text-accent-500">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-half-fill"></i>
                      <span className="text-foreground-700 font-semibold text-sm ml-2">4.5/5</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">Safety Verdict</p>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                      <i className="ri-shield-check-line"></i>
                      Legit &amp; Safe
                    </span>
                  </div>
                </div>
              </div>

              <h2>What Is Basetao And Who Is It For?</h2>
              <p>
                Before diving into the detailed review, it is important to understand what Basetao actually is and who can benefit from it. Basetao is a shopping platform that helps international buyers access products from various sources, with services including product sourcing, quality checking, warehouse storage, and international shipping. The <strong>Basetao Spreadsheet</strong> is a complementary resource that organizes product discoveries into easy-to-browse collections, making the entire shopping process more efficient and organized.
              </p>
              <p>
                The platform is designed for international shoppers who want to discover trending fashion items, streetwear, sneakers, accessories, and lifestyle products without spending hours manually searching through countless listings. Whether you are a casual browser curious about what is available or a serious buyer looking to build a curated wardrobe, Basetao offers tools and resources that can genuinely streamline your shopping experience.
              </p>

              <h2>Platform Experience: How Does Basetao Actually Work?</h2>
              <p>
                The user experience on Basetao is generally straightforward, though there is a learning curve if you are completely new to international shopping platforms. The interface prioritizes functionality over flashiness, which is actually a positive &mdash; the focus remains on helping you find products and manage your orders rather than distracting you with unnecessary design elements.
              </p>
              <p>
                Navigating through product categories is intuitive, and the <a href="/basetao-spreadsheet-guide" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">Basetao Spreadsheet</a> resource makes discovery significantly faster. Instead of clicking through pages of search results, the spreadsheet format lets you scan dozens of products at a glance, which is a genuine efficiency improvement over traditional browsing methods.
              </p>
              <p>
                One area where the platform could improve is the onboarding experience for first-time users. While the core functionality is solid, new users might benefit from more guided tutorials and explanatory content. This is actually where our <a href="/basetao-spreadsheet-guide" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">Basetao Spreadsheet Guide</a> comes in handy &mdash; it fills the knowledge gap and helps newcomers get up to speed quickly.
              </p>

              <h2>Product Selection: What Can You Actually Find?</h2>
              <p>
                The product selection available through Basetao is genuinely impressive. The platform provides access to a wide range of categories:
              </p>
              <ul>
                <li><strong>Shoes</strong> &mdash; Sneakers, casual shoes, athletic footwear spanning popular and niche brands. The shoe collection is consistently one of the strongest categories with frequent new additions.</li>
                <li><strong>Hoodies and Sweaters</strong> &mdash; A solid selection of pullover hoodies, zip-ups, crewneck sweaters, and seasonal knitwear that covers both streetwear and casual styles.</li>
                <li><strong>T-Shirts</strong> &mdash; From graphic tees with unique designs to high-quality blank basics, the t-shirt selection is broad and regularly updated with fresh arrivals.</li>
                <li><strong>Jackets and Outerwear</strong> &mdash; Bomber jackets, denim jackets, varsity styles, and seasonal outerwear options that cater to different tastes and budgets.</li>
                <li><strong>Accessories, Bags, Belts, and More</strong> &mdash; The accessories categories cover hats, socks, jewelry, phone cases, sunglasses, watches, backpacks, crossbody bags, totes, belts, perfumes, and various lifestyle products.</li>
              </ul>
              <p>
                The breadth of categories means most shoppers can find something that interests them, regardless of their specific style preferences. While no platform can cover absolutely everything, Basetao does an admirable job of maintaining a diverse and frequently refreshed product catalog.
              </p>

              <h2>Warehouse And Quality Control Service</h2>
              <p>
                One of the standout features of the Basetao platform is the warehouse and quality control (QC) service. When you place an order, products are first shipped to the Basetao warehouse where they undergo inspection before being forwarded to you internationally. This adds a valuable layer of protection &mdash; if a product arrives damaged, has quality issues, or does not match its description, the issue can be caught and addressed before the item ever leaves for international shipping.
              </p>
              <p>
                The warehouse process typically works in several stages: the product arrives at the warehouse, staff perform a visual inspection, QC photos may be taken and shared with you, and you have the opportunity to approve the item or flag any concerns. Once approved, the item is prepared for international shipment with appropriate packaging.
              </p>
              <p>
                This warehouse and QC process is particularly valuable for international shoppers who cannot easily return items. Having a local inspection point reduces the risk of receiving a disappointing product after waiting weeks for international delivery. It is genuinely one of the features that sets Basetao apart from simpler shopping platforms that lack this layer of buyer protection.
              </p>

              <h2>Shipping Costs: What To Expect</h2>
              <p>
                Shipping costs are always a significant consideration for international shoppers, and Basetao is no exception. The platform typically offers multiple shipping methods ranging from economy options (slower but more affordable) to express courier services (faster but more expensive). The exact costs depend on factors including the destination country, package weight and dimensions, and the chosen shipping method.
              </p>
              <p>
                Here is a general overview of what to expect:
              </p>

              <div className="overflow-x-auto my-8">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="px-5 py-3 text-left font-label font-semibold text-foreground-800 text-xs border-b border-background-200/70">Shipping Method</th>
                      <th className="px-5 py-3 text-left font-label font-semibold text-foreground-800 text-xs border-b border-background-200/70">Estimated Speed</th>
                      <th className="px-5 py-3 text-left font-label font-semibold text-foreground-800 text-xs border-b border-background-200/70">Cost Level</th>
                      <th className="px-5 py-3 text-left font-label font-semibold text-foreground-800 text-xs border-b border-background-200/70">Best For</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-background-200/50">
                      <td className="px-5 py-3 text-foreground-700 font-medium">Economy Line</td>
                      <td className="px-5 py-3 text-foreground-600">15-30 days</td>
                      <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">Low</span></td>
                      <td className="px-5 py-3 text-foreground-600">Non-urgent orders, budget-conscious shoppers</td>
                    </tr>
                    <tr className="border-b border-background-200/50">
                      <td className="px-5 py-3 text-foreground-700 font-medium">Standard Air</td>
                      <td className="px-5 py-3 text-foreground-600">10-20 days</td>
                      <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">Medium</span></td>
                      <td className="px-5 py-3 text-foreground-600">Balanced speed and cost</td>
                    </tr>
                    <tr className="border-b border-background-200/50">
                      <td className="px-5 py-3 text-foreground-700 font-medium">Express Courier</td>
                      <td className="px-5 py-3 text-foreground-600">5-10 days</td>
                      <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">High</span></td>
                      <td className="px-5 py-3 text-foreground-600">Urgent orders, high-value items</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p>
                While shipping costs can be a point of concern, it is important to view them in context. International shipping is inherently expensive, and Basetao shipping rates are generally competitive with other platforms offering similar services. The key is to factor shipping into your total cost calculation from the beginning rather than being surprised by it at checkout.
              </p>

              <h2>Is Basetao Legit? Safety And Trust Assessment</h2>
              <p>
                This is perhaps the most important question for anyone considering using the platform. Based on extensive research and analysis, the answer is yes &mdash; <strong>Basetao is a legitimate platform</strong>. It operates with transparent processes, provides real warehouse and QC services, and has a track record of fulfilling orders for international customers.
              </p>
              <p>
                That said, as with any online shopping platform, there are important safety practices to keep in mind:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8">
                <div className="p-5 rounded-xl bg-accent-50 border border-accent-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-shield-check-line text-sm"></i>
                    </span>
                    <h4 className="font-label font-semibold text-foreground-900 text-sm">Safety Checklist</h4>
                  </div>
                  <ul className="space-y-1.5">
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5"></i>
                      Use QC service for every order
                    </li>
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5"></i>
                      Review QC photos before approving shipment
                    </li>
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5"></i>
                      Start with a small test order
                    </li>
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-check-line text-accent-500 mt-0.5"></i>
                      Keep records of all communications
                    </li>
                  </ul>
                </div>
                <div className="p-5 rounded-xl bg-primary-50 border border-primary-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-alert-line text-sm"></i>
                    </span>
                    <h4 className="font-label font-semibold text-foreground-900 text-sm">Things To Watch</h4>
                  </div>
                  <ul className="space-y-1.5">
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-primary-500 mt-0.5"></i>
                      Understand shipping costs upfront
                    </li>
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-primary-500 mt-0.5"></i>
                      Be patient with international delivery times
                    </li>
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-primary-500 mt-0.5"></i>
                      Check product descriptions carefully
                    </li>
                    <li className="text-xs text-foreground-600 flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-primary-500 mt-0.5"></i>
                      Factor in all costs before ordering
                    </li>
                  </ul>
                </div>
              </div>

              <h2>Pros And Cons: An Honest Assessment</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                <div className="p-6 rounded-2xl bg-accent-50 border border-accent-200">
                  <h4 className="font-heading font-semibold text-foreground-900 text-lg mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-line text-accent-500"></i>
                    Pros
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-add-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Excellent product variety</strong> across multiple categories with regular updates and new arrivals</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-add-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Valuable warehouse and QC service</strong> that adds genuine buyer protection for international orders</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-add-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Basetao Spreadsheet is genuinely useful</strong> for organized product discovery and comparison shopping</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-add-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Multiple shipping options</strong> allow you to balance speed against cost based on your priorities</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-add-circle-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Free to browse and explore</strong> with no upfront cost or commitment required to start</span>
                    </li>
                  </ul>
                </div>
                <div className="p-6 rounded-2xl bg-secondary-50 border border-secondary-200">
                  <h4 className="font-heading font-semibold text-foreground-900 text-lg mb-4 flex items-center gap-2">
                    <i className="ri-thumb-down-line text-secondary-500"></i>
                    Cons
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-subtract-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Shipping costs can add up</strong> significantly, especially for heavier items or express delivery options</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-subtract-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Learning curve exists</strong> for first-time users unfamiliar with international shopping platforms</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-subtract-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Delivery times can be lengthy</strong> depending on the shipping method and destination country</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-subtract-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                      <span><strong>Onboarding could be better</strong> with more guided tutorials and explanatory content for newcomers</span>
                    </li>
                  </ul>
                </div>
              </div>

              <h2>Basetao vs Alternatives: How Does It Compare?</h2>
              <p>
                The international shopping platform space has several options, and it is worth understanding how Basetao compares to alternatives. While every platform has its strengths and weaknesses, Basetao differentiates itself through its comprehensive approach that combines product sourcing, quality control, warehouse service, and the unique Basetao Spreadsheet resource.
              </p>
              <p>
                Some alternative platforms may offer slightly lower fees on certain services or a more polished user interface. However, few combine the full range of services that Basetao provides, and the spreadsheet-based product discovery system is genuinely unique in this space. For shoppers who value organization and efficiency alongside product access, Basetao occupies a compelling position.
              </p>

              <h2>Frequently Asked Questions</h2>

              <div className="space-y-4 my-8">
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-label font-semibold text-foreground-900 text-sm mb-2">Is Basetao safe to use for international shopping?</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Yes, Basetao is a legitimate and safe platform for international shopping. The warehouse and quality control service adds an important layer of buyer protection. As with any online shopping, it is wise to start with a small test order to familiarize yourself with the process, use QC services, and keep records of your communications.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-label font-semibold text-foreground-900 text-sm mb-2">How much does Basetao shipping cost?</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Shipping costs vary based on destination country, package weight and dimensions, and chosen shipping method. Economy options are the most affordable (but slowest), while express courier services are fastest (but more expensive). It is best to calculate shipping costs for your specific order before committing.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-label font-semibold text-foreground-900 text-sm mb-2">What is the Basetao warehouse service?</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    The Basetao warehouse receives your ordered products, performs quality inspections, takes QC photos, and prepares items for international shipping. You can approve items after reviewing QC photos, and the warehouse will hold your items until you are ready to ship them. This service is particularly valuable for catching quality issues before items leave for international delivery.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-label font-semibold text-foreground-900 text-sm mb-2">Is there a Basetao coupon or discount code available?</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Discount availability varies over time. We recommend checking the main platform regularly for any current promotions, seasonal sales, or special offers. Some experienced shoppers also report that building a relationship with the platform over time can lead to loyalty benefits.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-label font-semibold text-foreground-900 text-sm mb-2">How does Basetao Spreadsheet help with shopping?</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    The <a href="/basetao-spreadsheet-guide" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">Basetao Spreadsheet</a> organizes product discoveries into categorized collections, making it much faster to browse, compare, and research products. Instead of manual searching, you can scan organized listings and jump directly to items that interest you. Read our complete guide for a detailed walkthrough.
                  </p>
                </div>
              </div>

              <h2>Final Verdict: Is Basetao Worth Using In 2026?</h2>
              <p>
                After a thorough evaluation of the platform, its services, and its unique resources, the conclusion is clear: <strong>Basetao is genuinely worth using for international shoppers</strong>, particularly those who value organization, efficiency, and buyer protection.
              </p>
              <p>
                The combination of product access, warehouse QC service, multiple shipping options, and the Basetao Spreadsheet resource creates a comprehensive ecosystem that addresses the major pain points of international shopping. While no platform is perfect, and users should maintain reasonable expectations around shipping costs and delivery times, Basetao delivers genuine value that justifies its place in the international shopping toolkit.
              </p>
              <p>
                For beginners, we recommend starting with our <a href="/basetao-spreadsheet-guide" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">Basetao Spreadsheet Complete Guide</a> to get oriented, placing a small test order to experience the process firsthand, and gradually building your familiarity with the platform. With a measured, informed approach, Basetao can become an invaluable part of your international shopping workflow.
              </p>

              <div className="mt-12 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 text-white text-center">
                <h3 className="text-xl md:text-2xl font-heading font-semibold mb-3">
                  Ready to Try Basetao For Yourself?
                </h3>
                <p className="text-white/85 text-sm mb-6 max-w-md mx-auto">
                  Explore the platform, browse curated Basetao Spreadsheet collections, and start your international shopping journey today.
                </p>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-accent-600 font-label font-semibold text-sm md:text-base hover:bg-white/90 transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line text-lg"></i>
                  Visit Basetao Platform
                </a>
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <p className="text-xs text-foreground-500 mb-3">Related Resources:</p>
                <div className="flex flex-wrap gap-3">
                  <a href="/" className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                    Homepage
                  </a>
                  <a href="/basetao-spreadsheet-guide" className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                    Basetao Spreadsheet Guide
                  </a>
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer">
                    Main Shopping Platform
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}